import { z } from 'zod';

// Schémas d'authentification
export const loginSchema = z.object({
  email: z.string()
    .email('Email invalide')
    .min(1, 'Email requis'),
  password: z.string()
    .min(6, 'Le mot de passe doit contenir au moins 6 caractères')
    .max(100, 'Mot de passe trop long'),
});

export const registerSchema = z.object({
  name: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(100, 'Nom trop long')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Le nom ne peut contenir que des lettres, espaces, apostrophes et tirets'),
  email: z.string()
    .email('Email invalide')
    .min(1, 'Email requis'),
  password: z.string()
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
    .max(100, 'Mot de passe trop long')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre'),
  confirmPassword: z.string(),
  role: z.enum(['PATIENT', 'DOCTOR', 'ADMIN'], {
    errorMap: () => ({ message: 'Rôle invalide' })
  }),
  phone: z.string()
    .regex(/^(\+33|0)[1-9](\d{8})$/, 'Numéro de téléphone français invalide')
    .optional(),
  city: z.string()
    .min(2, 'La ville doit contenir au moins 2 caractères')
    .max(100, 'Nom de ville trop long')
    .optional(),
  speciality: z.string()
    .min(2, 'La spécialité doit contenir au moins 2 caractères')
    .max(100, 'Spécialité trop longue')
    .optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

// Schémas de rendez-vous
export const appointmentSchema = z.object({
  patientId: z.string()
    .min(1, 'Patient requis'),
  doctorId: z.string()
    .min(1, 'Docteur requis'),
  appointmentDate: z.string()
    .min(1, 'Date requise')
    .refine((date) => {
      const appointmentDate = new Date(date);
      const now = new Date();
      return appointmentDate > now;
    }, 'La date doit être dans le futur'),
  appointmentTime: z.string()
    .regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Format d\'heure invalide (HH:MM)'),
  type: z.enum(['CONSULTATION', 'FOLLOW_UP', 'EMERGENCY', 'ROUTINE_CHECK'], {
    errorMap: () => ({ message: 'Type de rendez-vous invalide' })
  }),
  notes: z.string()
    .max(1000, 'Les notes ne peuvent pas dépasser 1000 caractères')
    .optional(),
  reason: z.string()
    .min(5, 'La raison doit contenir au moins 5 caractères')
    .max(500, 'Raison trop longue'),
});

export const updateAppointmentSchema = appointmentSchema.partial();

// Schémas de téléconsultation
export const teleconsultationSchema = z.object({
  patientId: z.string()
    .min(1, 'Patient requis')
    .optional(), // Injecté automatiquement par le backend via le token JWT
  doctorId: z.string()
    .min(1, 'Docteur requis')
    .optional(),
  symptoms: z.string()
    .min(3, 'Veuillez décrire vos symptômes')
    .max(2000, 'Description trop longue'),
  urgency: z.enum(['NORMAL', 'URGENT'], {
    errorMap: () => ({ message: 'Niveau d\'urgence invalide' })
  }),
  notes: z.string()
    .max(1000, 'Les notes ne peuvent pas dépasser 1000 caractères')
    .optional(),
  diagnosis: z.string()
    .optional(),
  prescription: z.string()
    .optional(),
  recommendations: z.string()
    .optional(),
  results: z.string()
    .optional(),
});

// Schémas de patient
export const patientSchema = z.object({
  name: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(100, 'Nom trop long'),
  email: z.string()
    .email('Email invalide'),
  phone_number: z.string()
    .regex(/^(\+33|0)[1-9](\d{8})$/, 'Numéro de téléphone français invalide')
    .optional(),
  date_of_birth: z.string()
    .refine((date) => {
      const birthDate = new Date(date);
      const now = new Date();
      const age = now.getFullYear() - birthDate.getFullYear();
      return age >= 0 && age <= 150;
    }, 'Date de naissance invalide')
    .optional(),
  blood_type: z.enum(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'], {
    errorMap: () => ({ message: 'Groupe sanguin invalide' })
  }).optional(),
  residence: z.string()
    .min(2, 'La ville doit contenir au moins 2 caractères')
    .max(100, 'Nom de ville trop long')
    .optional(),
});

// Schémas de recherche
export const patientSearchSchema = z.object({
  name: z.string()
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .optional(),
  email: z.string()
    .email('Email invalide')
    .optional(),
  shortNumber: z.string()
    .min(3, 'Le numéro court doit contenir au moins 3 caractères')
    .optional(),
  phone: z.string()
    .regex(/^(\+33|0)[1-9](\d{8})$/, 'Numéro de téléphone français invalide')
    .optional(),
  page: z.number()
    .min(1, 'La page doit être supérieure à 0')
    .optional(),
  limit: z.number()
    .min(1, 'La limite doit être supérieure à 0')
    .max(100, 'La limite ne peut pas dépasser 100')
    .optional(),
}).refine((data) => {
  // Au moins un critère de recherche doit être fourni
  return Object.values(data).some(value => value !== undefined && value !== '');
}, {
  message: 'Au moins un critère de recherche doit être fourni',
});

// Schémas de messagerie
export const messageSchema = z.object({
  recipientId: z.string()
    .min(1, 'Destinataire requis'),
  content: z.string()
    .min(1, 'Le message ne peut pas être vide')
    .max(2000, 'Message trop long'),
  type: z.enum(['TEXT', 'IMAGE', 'FILE', 'EMERGENCY', 'SYSTEM'], {
    errorMap: () => ({ message: 'Type de message invalide' })
  }),
  priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT'], {
    errorMap: () => ({ message: 'Priorité invalide' })
  }),
  encrypted: z.boolean()
    .default(true),
});

// Schémas MFA
export const mfaTokenSchema = z.object({
  token: z.string()
    .length(6, 'Le code MFA doit contenir exactement 6 chiffres')
    .regex(/^\d{6}$/, 'Le code MFA ne peut contenir que des chiffres'),
});

// Schémas d'appel vidéo
export const videoCallSchema = z.object({
  patientId: z.number()
    .min(1, 'ID patient invalide'),
  doctorId: z.number()
    .min(1, 'ID docteur invalide'),
  notes: z.string()
    .max(1000, 'Les notes ne peuvent pas dépasser 1000 caractères')
    .optional(),
});

// Types TypeScript dérivés des schémas
export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type AppointmentFormData = z.infer<typeof appointmentSchema>;
export type UpdateAppointmentFormData = z.infer<typeof updateAppointmentSchema>;
export type TeleconsultationFormData = z.infer<typeof teleconsultationSchema>;
export type PatientFormData = z.infer<typeof patientSchema>;
export type PatientSearchFormData = z.infer<typeof patientSearchSchema>;
export type MessageFormData = z.infer<typeof messageSchema>;
export type MfaTokenFormData = z.infer<typeof mfaTokenSchema>;
export type VideoCallFormData = z.infer<typeof videoCallSchema>;

// Fonction utilitaire pour valider les données
export const validateData = <T>(schema: z.ZodSchema<T>, data: unknown): { success: true; data: T } | { success: false; errors: string[] } => {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = error.errors.map(err => err.message);
      return { success: false, errors };
    }
    return { success: false, errors: ['Erreur de validation inconnue'] };
  }
};
